import { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, useReducer } from 'react';
import { noop, runIfFn, getOwnerDocument } from '@tonic-ui/utils';

/**
 * A custom Hook that creates a constant value over the lifecycle of a component.
 *
 * @param {(function|any)} value - The value to create a constant over.
 * @return {any} The constant value.
 */
var useConst = function useConst(init) {
  var _ref$current;
  var ref = useRef();
  if (ref.current === undefined) {
    // Wrap the value in an object so that we can tell if it's initialized even if the initializer is undefined or returns undefined
    ref.current = {
      value: typeof init === 'function' ? init() : init
    };
  }
  return ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.value;
};

function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return e;
  };
  var t,
    e = {},
    r = Object.prototype,
    n = r.hasOwnProperty,
    o = Object.defineProperty || function (t, e, r) {
      t[e] = r.value;
    },
    i = "function" == typeof Symbol ? Symbol : {},
    a = i.iterator || "@@iterator",
    c = i.asyncIterator || "@@asyncIterator",
    u = i.toStringTag || "@@toStringTag";
  function define(t, e, r) {
    return Object.defineProperty(t, e, {
      value: r,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), t[e];
  }
  try {
    define({}, "");
  } catch (t) {
    define = function (t, e, r) {
      return t[e] = r;
    };
  }
  function wrap(t, e, r, n) {
    var i = e && e.prototype instanceof Generator ? e : Generator,
      a = Object.create(i.prototype),
      c = new Context(n || []);
    return o(a, "_invoke", {
      value: makeInvokeMethod(t, r, c)
    }), a;
  }
  function tryCatch(t, e, r) {
    try {
      return {
        type: "normal",
        arg: t.call(e, r)
      };
    } catch (t) {
      return {
        type: "throw",
        arg: t
      };
    }
  }
  e.wrap = wrap;
  var h = "suspendedStart",
    l = "suspendedYield",
    f = "executing",
    s = "completed",
    y = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var p = {};
  define(p, a, function () {
    return this;
  });
  var d = Object.getPrototypeOf,
    v = d && d(d(values([])));
  v && v !== r && n.call(v, a) && (p = v);
  var g = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(p);
  function defineIteratorMethods(t) {
    ["next", "throw", "return"].forEach(function (e) {
      define(t, e, function (t) {
        return this._invoke(e, t);
      });
    });
  }
  function AsyncIterator(t, e) {
    function invoke(r, o, i, a) {
      var c = tryCatch(t[r], t, o);
      if ("throw" !== c.type) {
        var u = c.arg,
          h = u.value;
        return h && "object" == typeof h && n.call(h, "__await") ? e.resolve(h.__await).then(function (t) {
          invoke("next", t, i, a);
        }, function (t) {
          invoke("throw", t, i, a);
        }) : e.resolve(h).then(function (t) {
          u.value = t, i(u);
        }, function (t) {
          return invoke("throw", t, i, a);
        });
      }
      a(c.arg);
    }
    var r;
    o(this, "_invoke", {
      value: function (t, n) {
        function callInvokeWithMethodAndArg() {
          return new e(function (e, r) {
            invoke(t, n, e, r);
          });
        }
        return r = r ? r.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(e, r, n) {
    var o = h;
    return function (i, a) {
      if (o === f) throw new Error("Generator is already running");
      if (o === s) {
        if ("throw" === i) throw a;
        return {
          value: t,
          done: !0
        };
      }
      for (n.method = i, n.arg = a;;) {
        var c = n.delegate;
        if (c) {
          var u = maybeInvokeDelegate(c, n);
          if (u) {
            if (u === y) continue;
            return u;
          }
        }
        if ("next" === n.method) n.sent = n._sent = n.arg;else if ("throw" === n.method) {
          if (o === h) throw o = s, n.arg;
          n.dispatchException(n.arg);
        } else "return" === n.method && n.abrupt("return", n.arg);
        o = f;
        var p = tryCatch(e, r, n);
        if ("normal" === p.type) {
          if (o = n.done ? s : l, p.arg === y) continue;
          return {
            value: p.arg,
            done: n.done
          };
        }
        "throw" === p.type && (o = s, n.method = "throw", n.arg = p.arg);
      }
    };
  }
  function maybeInvokeDelegate(e, r) {
    var n = r.method,
      o = e.iterator[n];
    if (o === t) return r.delegate = null, "throw" === n && e.iterator.return && (r.method = "return", r.arg = t, maybeInvokeDelegate(e, r), "throw" === r.method) || "return" !== n && (r.method = "throw", r.arg = new TypeError("The iterator does not provide a '" + n + "' method")), y;
    var i = tryCatch(o, e.iterator, r.arg);
    if ("throw" === i.type) return r.method = "throw", r.arg = i.arg, r.delegate = null, y;
    var a = i.arg;
    return a ? a.done ? (r[e.resultName] = a.value, r.next = e.nextLoc, "return" !== r.method && (r.method = "next", r.arg = t), r.delegate = null, y) : a : (r.method = "throw", r.arg = new TypeError("iterator result is not an object"), r.delegate = null, y);
  }
  function pushTryEntry(t) {
    var e = {
      tryLoc: t[0]
    };
    1 in t && (e.catchLoc = t[1]), 2 in t && (e.finallyLoc = t[2], e.afterLoc = t[3]), this.tryEntries.push(e);
  }
  function resetTryEntry(t) {
    var e = t.completion || {};
    e.type = "normal", delete e.arg, t.completion = e;
  }
  function Context(t) {
    this.tryEntries = [{
      tryLoc: "root"
    }], t.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(e) {
    if (e || "" === e) {
      var r = e[a];
      if (r) return r.call(e);
      if ("function" == typeof e.next) return e;
      if (!isNaN(e.length)) {
        var o = -1,
          i = function next() {
            for (; ++o < e.length;) if (n.call(e, o)) return next.value = e[o], next.done = !1, next;
            return next.value = t, next.done = !0, next;
          };
        return i.next = i;
      }
    }
    throw new TypeError(typeof e + " is not iterable");
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, o(g, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), o(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, u, "GeneratorFunction"), e.isGeneratorFunction = function (t) {
    var e = "function" == typeof t && t.constructor;
    return !!e && (e === GeneratorFunction || "GeneratorFunction" === (e.displayName || e.name));
  }, e.mark = function (t) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(t, GeneratorFunctionPrototype) : (t.__proto__ = GeneratorFunctionPrototype, define(t, u, "GeneratorFunction")), t.prototype = Object.create(g), t;
  }, e.awrap = function (t) {
    return {
      __await: t
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, c, function () {
    return this;
  }), e.AsyncIterator = AsyncIterator, e.async = function (t, r, n, o, i) {
    void 0 === i && (i = Promise);
    var a = new AsyncIterator(wrap(t, r, n, o), i);
    return e.isGeneratorFunction(r) ? a : a.next().then(function (t) {
      return t.done ? t.value : a.next();
    });
  }, defineIteratorMethods(g), define(g, u, "Generator"), define(g, a, function () {
    return this;
  }), define(g, "toString", function () {
    return "[object Generator]";
  }), e.keys = function (t) {
    var e = Object(t),
      r = [];
    for (var n in e) r.push(n);
    return r.reverse(), function next() {
      for (; r.length;) {
        var t = r.pop();
        if (t in e) return next.value = t, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, e.values = values, Context.prototype = {
    constructor: Context,
    reset: function (e) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = t, this.done = !1, this.delegate = null, this.method = "next", this.arg = t, this.tryEntries.forEach(resetTryEntry), !e) for (var r in this) "t" === r.charAt(0) && n.call(this, r) && !isNaN(+r.slice(1)) && (this[r] = t);
    },
    stop: function () {
      this.done = !0;
      var t = this.tryEntries[0].completion;
      if ("throw" === t.type) throw t.arg;
      return this.rval;
    },
    dispatchException: function (e) {
      if (this.done) throw e;
      var r = this;
      function handle(n, o) {
        return a.type = "throw", a.arg = e, r.next = n, o && (r.method = "next", r.arg = t), !!o;
      }
      for (var o = this.tryEntries.length - 1; o >= 0; --o) {
        var i = this.tryEntries[o],
          a = i.completion;
        if ("root" === i.tryLoc) return handle("end");
        if (i.tryLoc <= this.prev) {
          var c = n.call(i, "catchLoc"),
            u = n.call(i, "finallyLoc");
          if (c && u) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          } else if (c) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
          } else {
            if (!u) throw new Error("try statement without catch or finally");
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          }
        }
      }
    },
    abrupt: function (t, e) {
      for (var r = this.tryEntries.length - 1; r >= 0; --r) {
        var o = this.tryEntries[r];
        if (o.tryLoc <= this.prev && n.call(o, "finallyLoc") && this.prev < o.finallyLoc) {
          var i = o;
          break;
        }
      }
      i && ("break" === t || "continue" === t) && i.tryLoc <= e && e <= i.finallyLoc && (i = null);
      var a = i ? i.completion : {};
      return a.type = t, a.arg = e, i ? (this.method = "next", this.next = i.finallyLoc, y) : this.complete(a);
    },
    complete: function (t, e) {
      if ("throw" === t.type) throw t.arg;
      return "break" === t.type || "continue" === t.type ? this.next = t.arg : "return" === t.type ? (this.rval = this.arg = t.arg, this.method = "return", this.next = "end") : "normal" === t.type && e && (this.next = e), y;
    },
    finish: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.finallyLoc === t) return this.complete(r.completion, r.afterLoc), resetTryEntry(r), y;
      }
    },
    catch: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.tryLoc === t) {
          var n = r.completion;
          if ("throw" === n.type) {
            var o = n.arg;
            resetTryEntry(r);
          }
          return o;
        }
      }
      throw new Error("illegal catch attempt");
    },
    delegateYield: function (e, r, n) {
      return this.delegate = {
        iterator: values(e),
        resultName: r,
        nextLoc: n
      }, "next" === this.method && (this.arg = t), y;
    }
  }, e;
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      var F = function () {};
      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var normalCompletion = true,
    didErr = false,
    err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

/**
 * A custom Hook that provides a copy method to copy text to the clipboard.
 * If anything does not work, it will output an error message and the value will be set to null.
 *
 * @return {[string, function]} The current value and a copy method.
 */
var useCopyToClipboard = function useCopyToClipboard() {
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var copy = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(text) {
      var _navigator;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if ((_navigator = navigator) !== null && _navigator !== void 0 && _navigator.clipboard) {
              _context.next = 3;
              break;
            }
            console.error('Clipboard API is not supported in this environment.');
            return _context.abrupt("return", false);
          case 3:
            _context.prev = 3;
            _context.next = 6;
            return navigator.clipboard.writeText(text);
          case 6:
            setValue(text);
            return _context.abrupt("return", true);
          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](3);
            console.error('Clipboard write failed: ', _context.t0);
            setValue();
            return _context.abrupt("return", false);
          case 15:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[3, 10]]);
    }));
    return function copy(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  return [value, copy];
};

/**
 * A modified version of the `useEffect` Hook that runs an effect only once.
 *
 * @param {function} effect - The effect to run.
 */
var useEffectOnce = function useEffectOnce(effect) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
};

/**
 * A custom Hook similar to `useEffectOnce`, but only runs once when a condition is met.
 *
 * @param {function} effect - The effect to run.
 * @param {boolean} when - The condition to run the effect.
 */
var useEffectOnceWhen = function useEffectOnceWhen(effect, when) {
  var runOnceRef = useRef(false);
  var effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(function () {
    if (!when || runOnceRef.current) {
      return;
    }
    if (typeof effectRef.current === 'function') {
      effectRef.current();
    }
    runOnceRef.current = true;
  }, [when]);
};

/**
 * A custom Hook that resolves to useEffect when "window" is not in scope and useLayoutEffect in the browser.
 */
var useIsomorphicEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * A custom Hook to do escape hatch optimization for event callbacks.
 *
 * @param {function} fn - The function to be called.
 * @param {Array} dependencies - The dependencies of the function.
 * @return {function} Returns a memoized version of the event callback.
 * @see https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
 * @see https://github.com/facebook/react/issues/14099
 */
var useEventCallback = function useEventCallback(fn, dependencies) {
  var ref = useRef(function () {
    throw new Error('Cannot call an event handler while rendering.');
  });

  /**
   * The useLayoutEffect function is triggered synchronously before the DOM mutations are painted.
   * However, the useEffect function is called after the DOM mutations are painted.
   */
  useIsomorphicEffect(function () {
    ref.current = fn;
  }, [fn].concat(_toConsumableArray(dependencies !== null && dependencies !== void 0 ? dependencies : [])));
  return useCallback(function () {
    var callback = ref.current;
    return callback.apply(void 0, arguments);
  }, [ref]);
};

/**
 * A custom Hook to manage browser event listeners.
 *
 * @param {HTMLElement} target - The target element to attach the event listener to.
 * @param {string} eventName - The name of the event to listen for.
 * @param {function} eventHandler - The event handler to call when the event is fired.
 * @param {object} [options] - Optional options to pass to the event handler.
 */
var useEventListener = function useEventListener(target, eventName, eventHandler, options) {
  var eventListener = useEventCallback(eventHandler);
  useEffect(function () {
    if (!eventHandler) {
      return noop;
    }
    var eventTarget = runIfFn(target);
    var isEventListenerSupported = typeof (eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.addEventListener) === 'function' && typeof (eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.removeEventListener) === 'function';
    if (!isEventListenerSupported) {
      return noop;
    }
    eventTarget.addEventListener(eventName, eventListener, options);
    return function () {
      eventTarget.removeEventListener(eventName, eventListener, options);
    };
  }, [target, eventName, eventHandler, eventListener, options]);
};

/**
 * A custom Hook that returns a boolean indicating if the component is hydrated.
 *
 * @return {boolean} A boolean indicating if the component is hydrated.
 *
 * • When doing Server-Side Rendering (SSR), the result will always be false.
 * • When doing Client-Side Rendering (CSR), the result will always be false on the first render, and true on subsequent renders.
 */
var useHydrated = function useHydrated() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isHydrated = _useState2[0],
    setHydrated = _useState2[1];
  useEffect(function () {
    setHydrated(true);
  }, []);
  return isHydrated;
};

/**
 * A custom Hook that returns a ref with the latest value.
 *
 * @param {any} value - The latest value.
 * @return {React.RefObject} Returns a ref object with the `current` property set to the latest value.
 */
var useLatestRef = function useLatestRef(value) {
  var ref = useRef(value);
  ref.current = value;
  return ref;
};

/**
 * A custom Hook that runs a callback at most once when a condition becomes true.
 *
 * @param {function} callback - The callback to run.
 * @param {boolean} when - The condition to run the callback.
 */
var useOnceWhen = function useOnceWhen(callback) {
  var when = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var ref = useRef(false);
  if (when && !ref.current) {
    if (typeof callback === 'function') {
      callback();
    }
    ref.current = true;
  }
};

/**
 * A custom Hook that runs a callback at most once.
 *
 * @param {function} callback - The callback to run.
 */
var useOnce = function useOnce(callback) {
  useOnceWhen(callback, true);
};

var useLatest = function useLatest(value) {
  useOnce(function () {
    console.error('Warning: The `useLatest` Hook has been renamed to `useLatestRef`.\n\nSee https://trendmicro-frontend.github.io/tonic-ui/react/latest/hooks for more information.');
  });
  return useLatestRef(value);
};

var getInitialState = function getInitialState(query, defaultValue) {
  if (defaultValue !== undefined) {
    return defaultValue;
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    var _window$matchMedia;
    return (_window$matchMedia = window.matchMedia(query)) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.matches;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[useMediaQuery] The default value should be defined for server-side rendering.');
  }
  return false;
};

/**
 * A cusom Hook that listens for matches to a CSS media query. It allows the rendering of components based on whether the media query matches or not.
 *
 * @param {string} query - The media query to match against.
 * @param {boolean} defaultValue - The default value to return if the media query is not matched.
 * @return {boolean} Returns a boolean value indicating whether the media query matches or not.
 */
var useMediaQuery = function useMediaQuery(query, defaultValue) {
  var _useState = useState(getInitialState(query, defaultValue)),
    _useState2 = _slicedToArray(_useState, 2),
    matches = _useState2[0],
    setMatches = _useState2[1];
  useEffect(function () {
    var _window, _window$matchMedia2;
    var mounted = true;
    var mql = (_window = window) === null || _window === void 0 ? void 0 : (_window$matchMedia2 = _window.matchMedia) === null || _window$matchMedia2 === void 0 ? void 0 : _window$matchMedia2.call(_window, query);
    var onChange = function onChange() {
      if (!mounted) {
        return;
      }
      setMatches(!!mql.matches);
    };
    if (!mql) {
      return noop;
    }
    mql.addEventListener('change', onChange);
    setMatches(!!mql.matches);
    return function () {
      mounted = false;
      mql.removeEventListener('change', onChange);
    };
  }, [query]);
  return matches;
};

/**
 * A custom Hook that merges React refs into a single memoized function.
 *
 * @param {...React.RefObject} refs
 */
var useMergeRefs = function useMergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return useMemo(function () {
    if (refs.every(function (ref) {
      return ref === null || ref === undefined;
    })) {
      return null;
    }
    return function (node) {
      refs.forEach(function (ref) {
        if (!ref) {
          return;
        }
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        try {
          ref.current = node;
        } catch (error) {
          throw new Error("Cannot assign value '".concat(node, "' to ref '").concat(ref, "'"));
        }
      });
    };
  }, refs); // eslint-disable-line react-hooks/exhaustive-deps
};

var defaultEvents = ['mousedown', 'touchstart'];

/**
 * Checks if a click happened outside a Ref. Handy for dropdowns, modals and popups etc.
 *
 * @param {React.RefObject} ref - A ref to the element to check if the click happened outside.
 * @param {function} handler - A function to call if the click happened outside the ref.
 * @param {Array<string>} [events] - An array of events to listen to. If `events` is empty, the hook will not listen to any events.
 */
var useOutsideClick = function useOutsideClick(ref, handler) {
  var events = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultEvents;
  var savedHandler = useEventCallback(function (event) {
    var _el$contains;
    var el = ref === null || ref === void 0 ? void 0 : ref.current;
    el && !(el !== null && el !== void 0 && (_el$contains = el.contains) !== null && _el$contains !== void 0 && _el$contains.call(el, event.target)) && handler(event);
  }, [handler]);
  useEffect(function () {
    if (!events) {
      return noop;
    }
    var filteredEvents = (Array.isArray(events) ? events : []).filter(function (x) {
      return typeof x === 'string';
    });
    var doc = getOwnerDocument(ref === null || ref === void 0 ? void 0 : ref.current);
    var _iterator = _createForOfIteratorHelper(filteredEvents),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _doc$addEventListener;
        var eventName = _step.value;
        doc === null || doc === void 0 ? void 0 : (_doc$addEventListener = doc.addEventListener) === null || _doc$addEventListener === void 0 ? void 0 : _doc$addEventListener.call(doc, eventName, savedHandler, true);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    return function () {
      var _iterator2 = _createForOfIteratorHelper(filteredEvents),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _doc$removeEventListe;
          var eventName = _step2.value;
          doc === null || doc === void 0 ? void 0 : (_doc$removeEventListe = doc.removeEventListener) === null || _doc$removeEventListe === void 0 ? void 0 : _doc$removeEventListe.call(doc, eventName, savedHandler, true);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    };
  }, [ref, savedHandler, events]);
};

/**
 * A custom Hook that stores the previous state or props.
 *
 * @param {any} value - The value to update the previous value with.
 * @return {any} The previous value.
 */
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
};

var defaultToggleReducer = function defaultToggleReducer(state, nextValue) {
  return typeof nextValue === 'boolean' ? nextValue : !state;
};

/**
 * A custom Hook that toggles between boolean values. It also accepts a toggle function that can be used to change the value.
 *
 * @param {boolean} initialValue - The initial value of the toggle.
 * @param {function} [toggleReducer] - An optional reducer function that can be used to determine the next value.
 * @return {[boolean, function]} Returns an array with the current value and a function to toggle the value.
 */
var useToggle = function useToggle() {
  var initialValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  var toggleReducer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultToggleReducer;
  return useReducer(toggleReducer, initialValue);
};

export { useConst, useCopyToClipboard, useEffectOnce, useEffectOnceWhen, useEventCallback, useEventListener, useHydrated, useIsomorphicEffect, useLatest, useLatestRef, useMediaQuery, useMergeRefs, useOnce, useOnceWhen, useOutsideClick, usePrevious, useToggle };
